import React, { useState, useEffect } from "react";
import { setToken } from "@/redux/authSlice";
import { useDispatch } from "react-redux";
import i18n from "@/lang/i18n";
import signInBg from "@/assets/backgrounds/PrimaryBg.svg";
import everestLogo from "@/assets/logos/everest.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import AuthService from "@/services/api/AuthService";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useNavigate } from "react-router-dom";
import { RotateCwIcon } from "lucide-react";
import { ExternalUserType } from "@/central-atoms/users/enums";
import { initializeFeatureFlag, storeActiveFleetId } from "@/utils/appInitializer";

const SignIn: React.FC = () => {
  const [phone, setPhone] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [error, setError] = useState("");
  const [requestOtpTimer, setRequestOtpTimer] = useState(0);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 10) {
      setPhone(value);
    }
  };

  const handleGetOtp = async () => {
    if (phone.length !== 10) {
      setError("Phone number must be exactly 10 digits.");
      return;
    }

    try {
      const phonePayload = `+91${phone}`;
      await AuthService.request_otp({ phone: phonePayload });
      setOtpRequested(true);
      setError("");
      setRequestOtpTimer(30);
    } catch (err) {
      setError("Failed to request OTP. Please try again.");
    }
  };

  useEffect(() => {
    if (requestOtpTimer > 0) {
      const countdown = setInterval(() => {
        setRequestOtpTimer((prevTimer) => prevTimer - 1);
      }, 1000);

      return () => clearInterval(countdown);
    }
  }, [requestOtpTimer]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center">
      {/* Cover Image */}
      <div className="relative w-full">
        <img
          src={signInBg}
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-4xl font-bold text-white px-8 leading-relaxed">
            {i18n.t("managingFleetsDayToDay")}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="my-5">
        <img src={everestLogo} alt="Everest Logo" className="w-24 h-24" />
      </div>
      <div className="w-full max-w-md px-10">
        {!otpRequested ? (
          <>
            <Label
              className="block text-sm font-medium text-gray-700 mb-2"
              htmlFor="phone-number"
            >
              {i18n.t("phoneNumber")}
            </Label>
            <Input
              variant="primary"
              className="px-4 py-2 mb-4"
              id="phone-number"
              type="text"
              value={phone}
              onChange={handlePhoneChange}
              placeholder={i18n.t("enterPhoneNumber")}
            />
            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
            <div className="flex items-center">
              <Button
                variant="primary"
                className="px-4 py-2"
                onClick={handleGetOtp}
                disabled={requestOtpTimer > 0}
              >
                {i18n.t("getOTP")}
              </Button>
              {requestOtpTimer > 0 && (
                <span className="ml-2 text-gray-500">{`${i18n.t("retryIn")} ${
                  requestOtpTimer < 10 ? `0${requestOtpTimer}` : requestOtpTimer
                }s`}</span>
              )}
            </div>
          </>
        ) : (
          <OTPInput phone={phone} />
        )}
      </div>
      <div className="mt-0 text-center">
        {otpRequested ? (
          <Button
            variant="link"
            className="text-red-400"
            onClick={() => setOtpRequested(false)}
          >
            {i18n.t("changePhoneNumber")}
          </Button>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

const OTPInput: React.FC<{ phone: string }> = ({ phone }) => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [resendOtpTimer, setResendOtpTimer] = useState(30);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleOtpChange = (value: string) => {
    setOtp(value);
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setError("OTP must be exactly 6 digits.");
      return;
    }

    try {
      const phonePayload = `+91${phone}`;
      const response = await AuthService.verify_otp({
        phone: phonePayload,
        otp,
      });

      const { user, token_info } = response.data;

      // Store the active fleet ID for future use
      if (user.active_fleet_id) {
        storeActiveFleetId(user.active_fleet_id);
        initializeFeatureFlag(user.active_fleet_id);
      }

      // Store auth token in localStorage
      localStorage.setItem("auth_token", token_info.auth_token);

      // Dispatch actions to store token in Redux
      dispatch(setToken(token_info)); 

      setError("");
      if (user.additional_props.external_user_props?.user_type === ExternalUserType.EverestDriver) {
        navigate("/driver-home");
      }
      else {
        navigate("/home");
      }
    } catch (err) {
      setError("Failed to verify OTP. Please try again.");
    }
  };

  const handleResendOtp = async () => {
    try {
      const phonePayload = `+91${phone}`;
      await AuthService.request_otp({ phone: phonePayload });
      setError("");
      setResendOtpTimer(30);
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    }
  };

  useEffect(() => {
    if (resendOtpTimer > 0) {
      const countdown = setInterval(() => {
        setResendOtpTimer((prevTimer) => prevTimer - 1);
      }, 1000);

      return () => clearInterval(countdown);
    }
  }, [resendOtpTimer]);

  return (
    <div className="flex flex-col items-center">
      <Label
        className="block text-sm font-medium text-gray-400 mb-1"
        htmlFor="otp"
      >
        {i18n.t("enterOTP")}
      </Label>
      <Label className="block text-sm font-medium text-gray-400 mb-6">
      {i18n.t("sentTo")} {phone}
      </Label>
      <InputOTP
        maxLength={6}
        pattern={REGEXP_ONLY_DIGITS}
        onChange={handleOtpChange}
      >
        {[...Array(6)].map((_, index) => (
          <InputOTPGroup variant="primary" key={index}>
            <InputOTPSlot variant="primary" index={index} />
          </InputOTPGroup>
        ))}
      </InputOTP>
      {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
      <Button
        variant="primary"
        className="px-4 py-2 mt-4"
        onClick={handleVerifyOtp}
      >
        {i18n.t("signIn")}
      </Button>

      <div className="my-4 flex items-center">
        <Button
          variant="link"
          onClick={handleResendOtp}
          disabled={resendOtpTimer > 0}
          className="text-gray-500"
        >
          <RotateCwIcon className="mr-1" size={18} />
          {i18n.t("resendOTP")}
        </Button>
        {resendOtpTimer > 0 && (
          <span className="ml-2 text-primary">{`00:${
            resendOtpTimer < 10 ? `0${resendOtpTimer}` : resendOtpTimer
          }`}</span>
        )}
      </div>
    </div>
  );
};

export default SignIn;
